import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box, Typography, Paper, Button, Select, MenuItem, FormControl,
  InputLabel, Chip, LinearProgress, Checkbox, FormControlLabel,
  TextField, Alert, CircularProgress, Slider, Divider, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  SlidersHorizontal, Play, CheckCircle, XCircle, Clock, ArrowRight,
  RotateCcw, History, ChevronRight, Lightbulb, Trophy
} from 'lucide-react';
import FeatureGate from '../../components/subscription/FeatureGate';
import UpgradePrompt from '../../components/subscription/UpgradePrompt';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const renderLatex = (text) => {
  if (!text) return '';
  if (typeof window !== 'undefined' && window.katex) {
    try {
      return text.replace(/\$\$(.*?)\$\$/g, (_, tex) =>
        window.katex.renderToString(tex, { displayMode: true, throwOnError: false })
      ).replace(/\$(.*?)\$/g, (_, tex) =>
        window.katex.renderToString(tex, { displayMode: false, throwOnError: false })
      );
    } catch (e) { return text; }
  }
  return text;
};

function SessionBuilder({ onStart, token }) {
  const [grade, setGrade] = useState(8);
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [difficulty, setDifficulty] = useState([]);
  const [questionCount, setQuestionCount] = useState(20);
  const [includeAnswered, setIncludeAnswered] = useState(true);
  const [retryWrongOnly, setRetryWrongOnly] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API_URL}/smart-study/topics?grade=${grade}`, { headers })
      .then(r => r.json()).then(d => { if (d.success) setTopics(d.data); })
      .catch(() => {});
  }, [grade]);

  const handlePreview = async () => {
    const res = await fetch(`${API_URL}/study-session/preview`, {
      method: 'POST', headers,
      body: JSON.stringify({ grade, topics: selectedTopics, difficulty, includeAnswered, retryWrongOnly })
    });
    const data = await res.json();
    if (data.success) setPreview(data.data.matchingQuestions);
  };

  const handleCreate = async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/study-session/create`, {
      method: 'POST', headers,
      body: JSON.stringify({ grade, topics: selectedTopics, difficulty, questionCount, includeAnswered, retryWrongOnly })
    });
    const data = await res.json();
    setLoading(false);
    if (data.success) onStart(data.data.sessionId);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SlidersHorizontal size={28} color="#6C63FF" />
        <Box>
          <Typography variant="h5" fontWeight={700}>Custom Study Session</Typography>
          <Typography variant="body2" color="text.secondary">Build a practice session with your own filters</Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Grade</InputLabel>
            <Select value={grade} label="Grade" onChange={e => { setGrade(e.target.value); setSelectedTopics([]); setPreview(null); }}>
              {[8,9,10,11,12].map(g => <MenuItem key={g} value={g}>Grade {g}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Topics</InputLabel>
            <Select multiple value={selectedTopics} label="Topics"
              onChange={e => { setSelectedTopics(e.target.value); setPreview(null); }}
              renderValue={sel => sel.length === 0 ? 'All' : sel.join(', ')}>
              {topics.map(t => <MenuItem key={t.topic} value={t.topic}>{t.topic} ({t.questionCount})</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Difficulty</InputLabel>
            <Select multiple value={difficulty} label="Difficulty"
              onChange={e => { setDifficulty(e.target.value); setPreview(null); }}
              renderValue={sel => sel.length === 0 ? 'All' : sel.join(', ')}>
              {['easy','medium','hard'].map(d => <MenuItem key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>Questions: {questionCount}</Typography>
          <Slider value={questionCount} onChange={(_, v) => setQuestionCount(v)} min={5} max={100} step={5}
            marks={[{value:5,label:'5'},{value:20,label:'20'},{value:50,label:'50'},{value:100,label:'100'}]}
            sx={{ maxWidth: 400 }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControlLabel control={<Checkbox checked={includeAnswered}
            onChange={e => { setIncludeAnswered(e.target.checked); if(e.target.checked) setRetryWrongOnly(false); setPreview(null); }} />}
            label="Include previously answered" />
          <FormControlLabel control={<Checkbox checked={retryWrongOnly}
            onChange={e => { setRetryWrongOnly(e.target.checked); if(e.target.checked) setIncludeAnswered(true); setPreview(null); }} />}
            label="Only questions I got wrong (retry mode)" />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button variant="outlined" onClick={handlePreview} sx={{ textTransform: 'none' }}>
            Preview Count
          </Button>
          {preview !== null && (
            <Typography variant="body2" color="text.secondary">
              {preview} questions match your filters
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={handleCreate} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Play size={16} />}
            sx={{ textTransform: 'none', px: 4 }}>
            Start Session
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

function ActiveSession({ sessionId, token, onComplete }) {
  const [session, setSession] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(Date.now());
  const [hintsShown, setHintsShown] = useState(0);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API_URL}/study-session/${sessionId}`, { headers })
      .then(r => r.json()).then(d => {
        if (d.success) { setSession(d.data); setCurrentIdx(d.data.answeredCount || 0); }
      }).finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!session) return <Alert severity="error">Session not found</Alert>;

  const q = session.questions[currentIdx];
  if (!q) {
    return (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Trophy size={48} color="#4caf50" />
        <Typography variant="h6" sx={{ mt: 2 }}>Session Complete!</Typography>
        <Button variant="contained" onClick={() => completeSession()} sx={{ mt: 2, textTransform: 'none' }}>
          See Results
        </Button>
      </Paper>
    );
  }

  const isMultipleChoice = q.options && q.options.length > 0;

  const submitAnswer = async () => {
    const answer = isMultipleChoice ? selectedOption : userAnswer.trim();
    if (!answer) return;
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    const correctAnswer = isMultipleChoice ? q.options.find(o => o.isCorrect)?.label : null;
    const isCorrect = correctAnswer ? answer === correctAnswer : answer.toLowerCase().trim() === (q.correctAnswer || '').toLowerCase().trim();

    const res = await fetch(`${API_URL}/study-session/${sessionId}/answer`, {
      method: 'POST', headers,
      body: JSON.stringify({ questionId: q._id, isCorrect, userAnswer: answer, timeSpent, hintsUsed: hintsShown })
    });
    const data = await res.json();
    if (data.success) {
      setLastResult({ isCorrect, correctAnswer: correctAnswer || q.correctAnswer });
      setShowResult(true);
      if (data.data.isComplete) setTimeout(() => completeSession(), 1500);
    }
  };

  const nextQuestion = () => {
    setCurrentIdx(prev => prev + 1);
    setShowResult(false);
    setLastResult(null);
    setUserAnswer('');
    setSelectedOption(null);
    setHintsShown(0);
    setStartTime(Date.now());
  };

  const completeSession = async () => {
    const res = await fetch(`${API_URL}/study-session/${sessionId}/complete`, { method: 'POST', headers });
    const data = await res.json();
    if (data.success) onComplete(data.data.summary);
  };

  const progress = ((currentIdx + (showResult ? 1 : 0)) / session.totalCount) * 100;

  return (
    <Box>
      <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, mb: 2 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Question {currentIdx + 1} of {session.totalCount}
      </Typography>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip label={q.topic} size="small" />
            <Chip label={q.difficulty || 'medium'} size="small" variant="outlined"
              color={q.difficulty === 'easy' ? 'success' : q.difficulty === 'hard' ? 'error' : 'warning'} />
          </Box>

          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}
            dangerouslySetInnerHTML={{ __html: renderLatex(q.questionText) }} />

          {q.hints && q.hints.length > 0 && !showResult && (
            <Box sx={{ mb: 2 }}>
              {Array.from({ length: hintsShown }).map((_, i) => (
                <Alert key={i} severity="info" icon={<Lightbulb size={16} />} sx={{ mb: 1 }}>
                  <Typography variant="body2" dangerouslySetInnerHTML={{
                    __html: renderLatex(typeof q.hints[i] === 'object' ? q.hints[i].text : q.hints[i])
                  }} />
                </Alert>
              ))}
              {hintsShown < q.hints.length && (
                <Button size="small" onClick={() => setHintsShown(h => h + 1)} sx={{ textTransform: 'none' }}>
                  Show hint ({hintsShown}/{q.hints.length})
                </Button>
              )}
            </Box>
          )}

          {!showResult ? (
            <>
              {isMultipleChoice ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {q.options.map(opt => (
                    <Paper key={opt.label} elevation={0} onClick={() => setSelectedOption(opt.label)}
                      sx={{ p: 2, cursor: 'pointer', border: '2px solid',
                        borderColor: selectedOption === opt.label ? 'primary.main' : 'divider',
                        borderRadius: 2, bgcolor: selectedOption === opt.label ? 'primary.50' : 'transparent' }}>
                      <Typography variant="body2"><strong>{opt.label}.</strong>{' '}
                        <span dangerouslySetInnerHTML={{ __html: renderLatex(opt.text) }} /></Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <TextField fullWidth placeholder="Your answer..." value={userAnswer}
                  onChange={e => setUserAnswer(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitAnswer(); }}
                  sx={{ mb: 2 }} autoFocus />
              )}
              <Button variant="contained" onClick={submitAnswer}
                disabled={isMultipleChoice ? !selectedOption : !userAnswer.trim()}
                endIcon={<ArrowRight size={16} />} sx={{ textTransform: 'none' }}>
                Check Answer
              </Button>
            </>
          ) : (
            <Box>
              <Alert severity={lastResult.isCorrect ? 'success' : 'error'} sx={{ mb: 2 }}
                icon={lastResult.isCorrect ? <CheckCircle size={20} /> : <XCircle size={20} />}>
                {lastResult.isCorrect ? 'Correct!' : `Incorrect — answer: ${lastResult.correctAnswer}`}
              </Alert>
              <Button variant="contained" onClick={nextQuestion} endIcon={<ChevronRight size={16} />}
                sx={{ textTransform: 'none' }}>Next</Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function SessionSummary({ summary, onNewSession }) {
  return (
    <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
      <Trophy size={48} color="#4caf50" />
      <Typography variant="h5" fontWeight={700} sx={{ mt: 2 }}>Session Complete</Typography>
      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', my: 3, flexWrap: 'wrap' }}>
        <Box><Typography variant="h4" fontWeight={700} color="primary">{summary.accuracy}%</Typography><Typography variant="caption">Accuracy</Typography></Box>
        <Box><Typography variant="h4" fontWeight={700}>{summary.totalCorrect}/{summary.totalAnswered}</Typography><Typography variant="caption">Correct</Typography></Box>
        <Box><Typography variant="h4" fontWeight={700}>{summary.avgTime}s</Typography><Typography variant="caption">Avg Time</Typography></Box>
      </Box>
      {summary.topicBreakdown && summary.topicBreakdown.length > 0 && (
        <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>By Topic</Typography>
          {summary.topicBreakdown.map(t => (
            <Box key={t.topic} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{t.topic}</Typography>
              <Typography variant="body2" fontWeight={600}>{t.correct}/{t.total} ({t.accuracy}%)</Typography>
            </Box>
          ))}
        </Box>
      )}
      <Button variant="contained" onClick={onNewSession} startIcon={<RotateCcw size={16} />} sx={{ textTransform: 'none' }}>
        New Session
      </Button>
    </Paper>
  );
}

function StudySessionContent() {
  const { token } = useAuth();
  const [view, setView] = useState('builder');
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [summary, setSummary] = useState(null);

  const handleStart = (sessionId) => { setActiveSessionId(sessionId); setView('active'); };
  const handleComplete = (sum) => { setSummary(sum); setView('summary'); };
  const handleNew = () => { setView('builder'); setActiveSessionId(null); setSummary(null); };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {view === 'builder' && <SessionBuilder onStart={handleStart} token={token} />}
      {view === 'active' && <ActiveSession sessionId={activeSessionId} token={token} onComplete={handleComplete} />}
      {view === 'summary' && <SessionSummary summary={summary} onNewSession={handleNew} />}
    </Box>
  );
}

export default function StudySessionPage() {
  return (
    <FeatureGate feature="custom_sessions" fallback={<UpgradePrompt feature="custom_sessions" />}>
      <StudySessionContent />
    </FeatureGate>
  );
}
